import {
  INamedDocument,
  isObject,
  type DocumentReference,
  type ISODateType,
  type ITimestampRange,
  type WithRef,
} from '@principle-theorem/shared';
import { AppointmentStatus, IAppointment } from '../appointment/appointment';
import { IAppointmentDependency } from '../appointment/appointment-dependency';
import { IAppointmentRequest } from '../appointment/appointment-request';
import { PrincipleCreatedBy } from '../common';
import { type IDepositMetadata } from '../deposit/deposit-metadata';
import {
  ICalendarEvent,
  ICandidateCalendarEvent,
  type ICalendarEventSchedule,
} from '../event/calendar-event';
import { type IEvent } from '../event/event';
import { IPatient } from '../patient/patient';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import { ITag } from '../tag/tag';
import { ITreatmentCategory } from '../treatment-category';

type CommonEventProperties = {
  label: string;
  pinnedNotes: string[];
  tags: INamedDocument<ITag>[];
  locked?: boolean;
  createdBy?: PrincipleCreatedBy;
};

type AppointmentProperties = CommonEventProperties & {
  patientRef: DocumentReference<IPatient>;
  status: AppointmentStatus;
  dependencies: IAppointmentDependency[];
  deposit?: IDepositMetadata;
  categoryRef?: DocumentReference<ITreatmentCategory>;
  appointmentRequestRef?: DocumentReference<IAppointmentRequest>;
  treatmentPlanName: string;
  treatmentStepName: string;
};

type CalendarEventProperties = CommonEventProperties & {
  scheduleRef?: DocumentReference<ICalendarEventSchedule>;
};

type GapProperties = CommonEventProperties & {
  candidates: WithRef<ICandidateCalendarEvent>[];
};

export type ScheduleSummaryEventMetadata<T extends object = object> =
  CommonEventProperties &
    (T extends IAppointment
      ? AppointmentProperties
      : T extends ICalendarEvent
        ? CalendarEventProperties
        : GapProperties);

export type AppointmentSummary = IScheduleSummaryEventable<IAppointment>;
export type CalendarEventSummary = IScheduleSummaryEventable<ICalendarEvent>;

export interface IScheduleSummaryEvent<T extends object = object> {
  event: IEvent;
  ref: DocumentReference<T>;
  isBlocking: boolean;
  metadata: ScheduleSummaryEventMetadata<T>;
}

export interface IScheduleSummaryEventable<T extends object = object>
  extends Omit<IScheduleSummaryEvent<T>, 'ref'> {
  ref?: DocumentReference<T>;
  uid: string;
}

export interface IScheduleSummaryTarget {
  staffer: DocumentReference<IStaffer>;
  practice: DocumentReference<IPractice>;
  day: ISODateType;
}

export interface IScheduleSummary<T extends object = object>
  extends IScheduleSummaryTarget {
  events: IScheduleSummaryEvent<T>[];
  gaps: ITimestampRange[];
}

export interface IScheduleSummaryUpsertAction {
  target: IScheduleSummaryTarget;
  data: IScheduleSummary;
  existing?: WithRef<IScheduleSummary>;
}

export function isAppointmentSummary(
  item: unknown
): item is AppointmentSummary {
  return (
    isObject(item) &&
    'event' in item &&
    'isBlocking' in item &&
    'metadata' in item &&
    isAppointmentMetadata(item.metadata)
  );
}

function isAppointmentMetadata(item: unknown): item is AppointmentProperties {
  return (
    isObject(item) &&
    'patientRef' in item &&
    'status' in item &&
    'dependencies' in item &&
    'treatmentPlanName' in item &&
    'treatmentStepName' in item
  );
}

export function isCalendarEventSummary(
  item: unknown
): item is CalendarEventSummary {
  return (
    isObject(item) &&
    'event' in item &&
    'isBlocking' in item &&
    'metadata' in item &&
    !isAppointmentMetadata(item.metadata)
  );
}
